import CustomTooltip from '../../../shared/CustomTooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import ChatIcon from '@mui/icons-material/Chat';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
    useCreateReservationChatMutation,
    useJoinBookingChannelMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import {
    JoinBookingChannel,
    ReservationChat,
} from '../../../../app/Slices/SevenDayApiTypes';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import FormSwitch from '../../../shared/FormSwitch';
import FormTextField from '../../../shared/FormTextField';
import toast from '../../../shared/Toast';
import PrivateMessages from './PrivateMessages';
import { theme } from '../../../../shared/theme';

interface ContactFormProps {
    reservation: {
        id: number;
        channelId?: string;
        privateMessages: { userId: number; channelId: string }[];
        owner?: { messagingUsers?: Array<number> };
    };
}

const Schema = z.object({
    message: z.string().min(1, { message: 'Required' }).max(5000, {
        message: 'Message cannot exceed 5000 characters',
    }),
    isPrivate: z.boolean(),
});

const ContactForm = ({ reservation }: ContactFormProps) => {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');

    const [createChat] = useCreateReservationChatMutation();
    const [joinChat] = useJoinBookingChannelMutation();

    const existingPrivateMessage = reservation?.privateMessages?.find(
        (m) => m?.userId === loggedInUser?.sub,
    );
    const existingPublicMessage = reservation?.channelId;

    const methods = useForm({
        resolver: zodResolver(Schema),
        defaultValues: { message: '', isPrivate: false },
    });

    const { control, handleSubmit, watch, setValue } = methods;
    const isPrivate = watch('isPrivate');

    useEffect(() => {
        if (existingPublicMessage && !existingPrivateMessage) {
            setValue('isPrivate', true);
        }
    }, [existingPublicMessage, existingPrivateMessage, setValue]);

    const [messageSending, setMessageSending] = useState(false);
    const [joining, setJoining] = useState(false);
    const navigate = useNavigate();

    const onSubmit = async (data: any) => {
        try {
            setMessageSending(true);
            const body: ReservationChat = {
                bookingId: reservation.id,
                message: data.message,
                isPrivate,
            };
            const response = await createChat(body).unwrap();
            navigate(`/messages/${response?.channelId}`);
        } catch {
            toast.error(
                `Error sending message to ${
                    loggedInUser?.role.includes('owner') ? 'guest' : 'host'
                }`,
            );
        } finally {
            setMessageSending(false);
        }
    };

    const onNavigate = async () => {
        if (!reservation?.channelId)
            return toast.error('Channel ID is required');
        try {
            setJoining(true);
            const body: JoinBookingChannel = {
                bookingId: reservation.id,
                channelId: reservation?.channelId,
            };
            await joinChat(body).unwrap();
            navigate(`/messages/${reservation?.channelId}`);
        } catch {
            toast.error('Error joining channel');
        } finally {
            setJoining(false);
        }
    };

    const onNavigatePrivate = async () => {
        try {
            navigate(`/messages/${existingPrivateMessage?.channelId}`);
        } catch {
            toast.error('Error opening private chat');
        }
    };

    const titleText = reservation?.channelId
        ? 'Messages'
        : `Contact the ${loggedInUser?.role.includes('owner') ? 'Guest' : 'Host'}`;

    const canShowPrivateToggle =
        !loggedInUser?.role.includes('owner') &&
        !existingPrivateMessage &&
        !reservation?.channelId;

    const introText = isPrivate
        ? 'Message the host privately below.'
        : existingPublicMessage
          ? 'View your messages.'
          : loggedInUser?.role.includes('owner')
            ? 'Send guests key details or a welcome note.'
            : 'Got a question? Message the host.';

    const showComposer =
        (!existingPrivateMessage && !loggedInUser?.role.includes('owner')) ||
        !existingPublicMessage;

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 0.75,
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography
                        component='h2'
                        variant='body1'
                        sx={{ fontWeight: 700, letterSpacing: 0.2 }}
                    >
                        {titleText}
                    </Typography>
                </Box>

                {showComposer ? (
                    <>
                        <Typography variant='body3' sx={{ mb: 1 }}>
                            {introText}
                        </Typography>

                        <FormTextField
                            name='message'
                            control={control}
                            rows={3}
                            multiline
                            sx={{ backgroundColor: 'white' }}
                        />
                        <Box>
                            {canShowPrivateToggle ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        paddingLeft: 0.75,
                                        paddingTop: 0.75,
                                    }}
                                >
                                    <FormSwitch
                                        name='isPrivate'
                                        control={control}
                                        label='Private Message'
                                        small
                                        labelSx={{
                                            fontSize: 12,
                                            // fontWeight: 600,
                                        }}
                                    />
                                    <CustomTooltip
                                        title='Send a message only visible to the host. Guests invited on this experience will not see the message.'
                                        placement='left'
                                    >
                                        <HelpOutlineIcon
                                            sx={{
                                                color: 'text.primary',
                                                fontSize: 12,
                                            }}
                                        />
                                    </CustomTooltip>
                                </Box>
                            ) : null}
                        </Box>

                        <Box pt={1}>
                            <CustomButton
                                loading={messageSending}
                                disabled={messageSending}
                                variant='outlined'
                                color='primary'
                                type='submit'
                                sx={{
                                    borderRadius: 2,
                                    height: 34,
                                    minWidth: 150,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                }}
                                fullWidth
                            >
                                Send
                            </CustomButton>
                        </Box>
                    </>
                ) : null}

                {reservation?.channelId ? (
                    <Stack sx={{ pt: 2 }}>
                        <Typography variant='body3' sx={{ mb: 1 }}>
                            You have an existing group chat{' '}
                            <Box
                                component='span'
                                onClick={onNavigate}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    ml: 0.5,
                                    color: theme.palette.text.primary,
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                <ChatIcon sx={{ fontSize: 14 }} />
                            </Box>
                        </Typography>
                    </Stack>
                ) : null}

                {existingPrivateMessage &&
                !loggedInUser?.role.includes('owner') ? (
                    <Stack sx={{ pt: 2 }}>
                        <Typography variant='body3' sx={{ mb: 1 }}>
                            You have an existing private chat{' '}
                            <Box
                                component='span'
                                onClick={onNavigatePrivate}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    ml: 0.5,
                                    color: theme.palette.text.primary,
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                <ChatIcon sx={{ fontSize: 14 }} />
                            </Box>
                        </Typography>
                    </Stack>
                ) : null}

                {loggedInUser?.role.includes('owner') &&
                reservation?.privateMessages?.length > 0 ? (
                    <Stack sx={{ pt: 2 }}>
                        <Typography variant='body3' sx={{ mb: 1 }}>
                            You have private conversations with:
                        </Typography>

                        <PrivateMessages
                            messages={reservation.privateMessages}
                            reservationId={reservation.id}
                        />
                    </Stack>
                ) : null}
            </form>
        </FormProvider>
    );
};

export default ContactForm;
